import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "~/utils/trpc";
import { useState, useEffect } from "react";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Input } from "@quake/shared-ui/components/ui/input";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import { Checkbox } from "@quake/shared-ui/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import { makeAttributeId, type JobStatus, type JobType, type StatusType } from "@quake/isomorphic";
import { StatusTypeSelect } from "~/components/base/StatusTypeSelect";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import { toast } from "@quake/shared-ui/lib/sonner";
import { Separator } from "@quake/shared-ui/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@quake/shared-ui/components/ui/table";
import { Label } from "@quake/shared-ui/components/ui/label";
import { ColorPicker } from "@quake/shared-ui/components/ui/color-picker";
import type { AttrType } from "@quake/isomorphic/dataFilters";
import { AttributesDialog } from "~/components/AttributesDialog";
import { DeleteConfirmationDialog } from "~/components/DeleteConfirmationDialog";
import GripVerticalIcon from "lucide-react/dist/esm/icons/grip-vertical";
import { cn } from "@quake/shared-ui/lib/utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import type { JobLabels } from "@quake/isomorphic/schemas/job-labels.schema";
import { JobLabelsCard } from "~/components/jobs/job-labels-card";
import { useJobLabels } from "~/hooks/useJobLabels";
import { useSubmissionLabels } from "~/hooks/useSubmissionLabels";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/jobs/settings")({
  component: RouteComponent,
});

let statusIdCounter = 0;
const createStatusId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${statusIdCounter++}`;

function RouteComponent() {
  const submissionTerm = useSubmissionLabels();
  const { data: session, isLoading: isSessionLoading } = useQuery(trpc.auth.getSession.queryOptions());
  const { data: settings } = useQuery(trpc.jobsSettings.get.queryOptions());
  const update = useMutation(
    trpc.jobsSettings.update.mutationOptions({ onError: (error) => toast.error(error.message) }),
  );
  const [statuses, setStatuses] = useState<JobStatus[]>([]);
  const [types, setTypes] = useState<JobType[]>([]);
  const [newStatus, setNewStatus] = useState({
    name: "",
    description: "",
    color: "#2196F3",
    type: "active" as StatusType,
  });
  const [defaultStatusId, setDefaultStatusId] = useState<string | null>(null);
  const [newType, setNewType] = useState({ name: "", description: "", color: "#2196F3" });
  const [defaultTypeId, setDefaultTypeId] = useState<string | null>(null);
  const [requireType, setRequireType] = useState(false);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [labels, setLabels] = useState<JobLabels>({});
  const jobLabels = useJobLabels();
  const qc = useQueryClient();

  const attr: AttrType = {
    id: "",
    name: "",
    description: "",
    searchable: false,
    type: "Text",
    table_reference: "",
    options: [] as { value: string; label: string; index: number }[],
    defaultValue: "",
    boundaries: { required: false, unique: false },
  };

  const entityTypes = [
    "Text",
    "Number",
    "Checkbox",
    "Date",
    "TimeStamp",
    "Select",
    "Multi Select",
    "Record",
    "Multi Record",
  ] as const;

  const optionsList = ["Select", "Multi Select"] as const;

  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [selectedAttribute, setSelectedAttribute] = useState(attr);
  const [attrIndex, setAttrIndex] = useState(-1);
  const [attributeToDeleteIndex, setAttributeToDeleteIndex] = useState<number | null>(null);
  const { datasetSlug } = useParams({ from: "/_dashboard/$datasetSlug" });
  const canCreateAttributes = session?.user?.role === "Administrator";
  useEffect(() => {
    const nextStatuses = settings?.statuses || [];
    setStatuses(nextStatuses);
    setDefaultStatusId(settings?.default_status_id || nextStatuses[0]?.id || null);
    setTypes(settings?.types || []);
    setDefaultTypeId(settings?.default_type_id || null);
    setRequireType(settings?.require_type || false);
    setAttributes(settings?.attributes || []);
    setLabels(settings?.labels ?? {});
  }, [settings]);

  useBreadCrumbs([
    { title: jobLabels.plural, link: `/${datasetSlug}/data/jobs` },
    { title: "Settings", link: `/${datasetSlug}/data/jobs/settings` },
  ]);

  const save = () =>
    update.mutate(
      {
        statuses,
        types,
        default_status_id: defaultStatusId,
        default_type_id: defaultTypeId,
        require_type: requireType,
        attributes,
        labels,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            qc.invalidateQueries({ queryKey: trpc.jobsSettings.get.queryOptions().queryKey }),
            qc.invalidateQueries({ queryKey: trpc.data.getRecruitmentJobs.queryKey() }),
            qc.invalidateQueries({ queryKey: trpc.entityViews.getViews.queryKey({ entityType: "recruitment_jobs" }) }),
            qc.invalidateQueries({ queryKey: trpc.entityViews.getViewBySlug.queryKey() }),
            qc.invalidateQueries({
              queryKey: trpc.entityViews.getAvailableFields.queryKey({ entityType: "recruitment_jobs" }),
            }),
          ]);
          toast.success(`${jobLabels.singular} settings saved successfully`);
        },
      },
    );

  const updateStatus = (index: number, data: Partial<JobStatus>) => {
    setStatuses((prev) => {
      const list = [...prev];
      list[index] = { ...list[index], ...data } as JobStatus;
      return list;
    });
  };

  const removeStatus = (index: number) => {
    setStatuses((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (prev[index]?.id === defaultStatusId) setDefaultStatusId(next[0]?.id || null);
      return next;
    });
  };

  const addStatus = () => {
    const trimmedName = newStatus.name.trim();
    if (!trimmedName) return;
    const id = createStatusId();
    setStatuses([
      ...statuses,
      {
        id,
        name: trimmedName,
        description: newStatus.description,
        color: newStatus.color,
        type: newStatus.type,
      },
    ]);
    if (!defaultStatusId) setDefaultStatusId(id);
    setNewStatus({ name: "", description: "", color: "#2196F3", type: "active" as StatusType });
  };

  const updateType = (index: number, data: Partial<JobType>) => {
    setTypes((prev) => {
      const list = [...prev];
      list[index] = { ...list[index], ...data } as JobType;
      return list;
    });
  };

  const removeType = (index: number) => {
    setTypes((prev) => prev.filter((_, i) => i !== index));
  };

  const addType = () => {
    const trimmedName = newType.name.trim();
    if (!trimmedName) return;
    setTypes([
      ...types,
      {
        id: crypto.randomUUID(),
        name: trimmedName,
        description: newType.description,
        color: newType.color,
      },
    ]);
    setNewType({ name: "", description: "", color: "#2196F3" });
  };

  const removeAttribute = (index: number) => {
    setAttributeToDeleteIndex(index);
  };

  const confirmDeleteAttribute = () => {
    if (attributeToDeleteIndex !== null) {
      setAttributes((prev) => prev.filter((_, i) => i !== attributeToDeleteIndex));
      setAttributeToDeleteIndex(null);
    }
  };

  const handleAddClick = () => {
    setDialogMode("add");
    setSelectedAttribute(attr);
    setAttributeDialogOpen(true);
  };

  const handleEditClick = (a: typeof attr, idx: number) => {
    setDialogMode("edit");
    setSelectedAttribute(a);
    setAttrIndex(idx);
    setAttributeDialogOpen(true);
  };

  const handleDialogSave = (newAttr: typeof attr) => {
    let list = [...attributes];
    const finalId = newAttr.id?.trim() || makeAttributeId(newAttr.name);
    const resolvedAttr = { ...newAttr, id: finalId };
    if (dialogMode === "edit" && attrIndex !== -1) {
      list[attrIndex] = resolvedAttr;
    } else {
      list = [...list, resolvedAttr];
    }
    setAttributes(list);
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  return (
    <div className="space-y-4 mx-5 pb-8">
      <h1 className="text-2xl font-bold">{jobLabels.plural} Settings</h1>
      <JobLabelsCard labels={labels} onChange={setLabels} />
      <Card>
        <CardHeader>
          <CardTitle>{submissionTerm.singular} stages have moved</CardTitle>
          <CardDescription>
            Manage stages, allowed moves, required steps, and action labels in Settings → {submissionTerm.plural}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline" className="cursor-pointer">
            <Link to="/$datasetSlug/settings/submissions" params={{ datasetSlug }}>
              Go to {submissionTerm.singular} settings
            </Link>
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Job Statuses</CardTitle>
          <CardDescription>
            Job statuses define the current state of a job posting at any given time. These help you track whether a
            position is actively being recruited for, temporarily paused, or has been filled. Common statuses include
            "Open" for active recruitment, "On Hold" for temporarily paused positions, "Closed" when the role has been
            filled, and "Cancelled" for positions that are no longer needed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mt-4">
            <Input
              value={newStatus.name}
              onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
              placeholder="New status"
              className="w-32"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addStatus();
                }
              }}
            />
            <Input
              value={newStatus.description}
              onChange={(e) => setNewStatus({ ...newStatus, description: e.target.value })}
              placeholder="Description"
            />
            <ColorPicker
              selectedColor={newStatus.color}
              onColorChange={(color) => setNewStatus({ ...newStatus, color })}
            />
            <StatusTypeSelect value={newStatus.type} onChange={(type) => setNewStatus((prev) => ({ ...prev, type }))} />
            <Button type="button" onClick={addStatus}>
              Add
            </Button>
          </div>
          <ul className="space-y-2 mt-4">
            {statuses.map((s, i) => (
              <li key={i} className="flex items-center gap-2">
                <ColorPicker selectedColor={s.color} onColorChange={(color) => updateStatus(i, { color })} />
                <Input value={s.name} onChange={(e) => updateStatus(i, { name: e.target.value })} className="w-32" />
                <Input value={s.description || ""} onChange={(e) => updateStatus(i, { description: e.target.value })} />
                <StatusTypeSelect value={s.type} onChange={(type) => updateStatus(i, { type })} />
                <Trash2 className="h-4 w-4 cursor-pointer" onClick={() => removeStatus(i)} />
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Label>Default Job Status</Label>
            <Select value={defaultStatusId || undefined} onValueChange={(v) => setDefaultStatusId(v || null)}>
              <SelectTrigger className="w-60">
                <SelectValue placeholder="Select default job status" />
              </SelectTrigger>
              <SelectContent>
                {statuses
                  .filter((s): s is JobStatus & { id: string } => Boolean(s.id))
                  .map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Job Types</CardTitle>
          <CardDescription>
            Job types categorize the nature of the employment, such as "Permanent", "Contract", or "Part-Time".
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mt-4">
            <Input
              value={newType.name}
              onChange={(e) => setNewType({ ...newType, name: e.target.value })}
              placeholder="New type"
              className="w-32"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addType();
                }
              }}
            />
            <Input
              value={newType.description}
              onChange={(e) => setNewType({ ...newType, description: e.target.value })}
              placeholder="Description"
            />
            <ColorPicker selectedColor={newType.color} onColorChange={(color) => setNewType({ ...newType, color })} />
            <Button type="button" onClick={addType}>
              Add
            </Button>
          </div>
          <ul className="space-y-2 mt-4">
            {types.map((t, i) => (
              <li key={t.id || i} className="flex items-center gap-2">
                <ColorPicker selectedColor={t.color} onColorChange={(color) => updateType(i, { color })} />
                <Input value={t.name} onChange={(e) => updateType(i, { name: e.target.value })} className="w-32" />
                <Input value={t.description || ""} onChange={(e) => updateType(i, { description: e.target.value })} />
                <Trash2 className="h-4 w-4 cursor-pointer" onClick={() => removeType(i)} />
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Label>Default Job Type</Label>
            <Select value={defaultTypeId || undefined} onValueChange={(v) => setDefaultTypeId(v || null)}>
              <SelectTrigger className="w-60">
                <SelectValue placeholder="Select default job type" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id || ""}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Checkbox id="require-type" checked={requireType} onCheckedChange={(v) => setRequireType(!!v)} />
            <Label htmlFor="require-type" className="text-sm text-muted-foreground cursor-pointer">
              Require job type when creating jobs
            </Label>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Custom Attributes</CardTitle>
          <CardDescription>
            Configure additional fields for jobs. These attributes will appear in the job form.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canCreateAttributes ? (
            <div className="text-right">
              <Button type="button" onClick={handleAddClick}>
                <PlusIcon className="mr-1 h-4 w-4" /> Add Attribute
              </Button>
            </div>
          ) : isSessionLoading ? (
            <Alert>
              <AlertDescription>Loading session information...</AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertDescription>Only administrators can create job custom attributes.</AlertDescription>
            </Alert>
          )}
          <AttributesDialog
            open={attributeDialogOpen}
            onOpenChange={setAttributeDialogOpen}
            attribute={selectedAttribute}
            onSave={handleDialogSave}
            onCancel={handleDialogCancel}
            mode={dialogMode}
            optionsList={optionsList}
            entityTypes={entityTypes}
            existingNames={attributes
              .filter((a, idx) => dialogMode === "add" || idx !== attrIndex)
              .map((a) => a.name.trim())}
            existingIds={attributes
              .filter((a, idx) => dialogMode === "add" || idx !== attrIndex)
              .map((a) => a.id?.trim() || makeAttributeId(a.name))}
          />

          <AttributesList
            attributes={attributes}
            onReorder={(newAttributes) => setAttributes(newAttributes)}
            onEdit={(index, attribute) => handleEditClick(attribute, index)}
            onDelete={(index) => removeAttribute(index)}
          />
        </CardContent>
      </Card>
      {update.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {update.error instanceof Error ? update.error.message : "Failed to save job settings. Please try again."}
          </AlertDescription>
        </Alert>
      )}
      <Button onClick={save}>Save</Button>

      <Separator className="my-6" />

      <DeleteConfirmationDialog
        open={attributeToDeleteIndex !== null}
        onOpenChange={(open) => !open && setAttributeToDeleteIndex(null)}
        onConfirm={confirmDeleteAttribute}
        description={`This will permanently delete the attribute "${
          attributeToDeleteIndex !== null ? attributes[attributeToDeleteIndex]?.name : ""
        }". This action cannot be undone.`}
      />
    </div>
  );
}

type AttributeType = AttrType;

function AttributesList({
  attributes,
  onReorder,
  onEdit,
  onDelete,
}: {
  attributes: AttributeType[];
  onReorder: (attributes: AttributeType[]) => void;
  onEdit: (index: number, attribute: AttributeType) => void;
  onDelete: (index: number) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = attributes.findIndex((attr) => (attr.id || makeAttributeId(attr.name)) === active.id);
      const newIndex = attributes.findIndex((attr) => (attr.id || makeAttributeId(attr.name)) === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(attributes, oldIndex, newIndex));
      }
    }
  };

  const attributeIds = attributes.map((attr) => attr.id || makeAttributeId(attr.name));

  if (attributes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground border rounded-md">
        No attributes yet. Click "Add Attribute" to get started.
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <div className="overflow-x-auto border rounded-md">
        <Table useDynamicHeight={false}>
          <TableHeader>
            <TableRow className="bg-muted-foreground/5">
              <TableHead className="w-12"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Required</TableHead>
              <TableHead>Unique</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <SortableContext items={attributeIds} strategy={verticalListSortingStrategy}>
              {attributes.map((attribute, index) => (
                <SortableAttributeRow
                  key={attribute.id || makeAttributeId(attribute.name)}
                  attribute={attribute}
                  index={index}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </SortableContext>
          </TableBody>
        </Table>
      </div>
    </DndContext>
  );
}

function SortableAttributeRow({
  attribute,
  index,
  onEdit,
  onDelete,
}: {
  attribute: AttributeType;
  index: number;
  onEdit: (index: number, attribute: AttributeType) => void;
  onDelete: (index: number) => void;
}) {
  const attrId = attribute.id || makeAttributeId(attribute.name);
  const {
    attributes: sortableAttributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: attrId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={cn(isDragging && "shadow-lg bg-accent/50")}>
      <TableCell className="w-12">
        <div className="w-6 cursor-grab active:cursor-grabbing" {...listeners} {...sortableAttributes}>
          <GripVerticalIcon className="h-5 w-5 text-muted-foreground" />
        </div>
      </TableCell>
      <TableCell className="font-medium">{attribute.name}</TableCell>
      <TableCell>
        <code>{attrId}</code>
      </TableCell>
      <TableCell>{attribute.type}</TableCell>
      <TableCell>{attribute.boundaries?.required ? "Yes" : "No"}</TableCell>
      <TableCell>{attribute.boundaries?.unique ? "Yes" : "No"}</TableCell>
      <TableCell className="w-28 text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Edit attribute ${attribute.name}`}
            onClick={() => onEdit(index, attribute)}
          >
            <EditIcon size={18} className="text-green-500" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Delete attribute ${attribute.name}`}
            onClick={() => onDelete(index)}
          >
            <Trash2 size={18} className="stroke-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
